import React, { useState, useEffect } from 'react';
import Header from '../components/layout/Header';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { transactionsAPI, casesAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Plus, 
  Search, 
  Filter,
  Edit,
  Trash2,
  TrendingUp,
  TrendingDown,
  DollarSign,
  ArrowUpCircle,
  ArrowDownCircle,
  BarChart3,
  PieChart,
  Users,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Calendar,
  FileDown,
  Download
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart as RePieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  Area,
  AreaChart
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FinancePage = () => {
  const { t, language } = useLanguage();
  const { canManageFinance, isAdmin, token } = useAuth();
  const [transactions, setTransactions] = useState([]);
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Analytics data
  const [dashboard, setDashboard] = useState(null);
  const [analytics, setAnalytics] = useState([]);
  const [employeePerformance, setEmployeePerformance] = useState([]);
  const [expenseBreakdown, setExpenseBreakdown] = useState(null);
  const [alerts, setAlerts] = useState([]);
  
  const [formData, setFormData] = useState({
    transaction_type: 'income',
    amount: '',
    description: '',
    case_id: '',
    category: '',
    date: new Date().toISOString().split('T')[0]
  });

  const api = axios.create({
    baseURL: `${API_URL}/api`,
    headers: { Authorization: `Bearer ${token}` }
  });

  const categories = {
    income: [
      { value: 'consultation', label: language === 'ar' ? 'استشارة' : 'Consultation' },
      { value: 'case_fee', label: language === 'ar' ? 'أتعاب قضية' : 'Case Fee' },
      { value: 'retainer', label: language === 'ar' ? 'رسوم التوكيل' : 'Retainer' },
      { value: 'other', label: language === 'ar' ? 'أخرى' : 'Other' },
    ],
    expense: [
      { value: 'salary', label: language === 'ar' ? 'رواتب' : 'Salaries' },
      { value: 'marketing', label: language === 'ar' ? 'تسويق' : 'Marketing' },
      { value: 'rent', label: language === 'ar' ? 'إيجار ومكتب' : 'Rent & Office' },
      { value: 'legal_fees', label: language === 'ar' ? 'مصاريف قانونية' : 'Legal Fees' },
      { value: 'operations', label: language === 'ar' ? 'تشغيل عام' : 'Operations' },
      { value: 'utilities', label: language === 'ar' ? 'خدمات' : 'Utilities' },
      { value: 'travel', label: language === 'ar' ? 'سفر' : 'Travel' },
      { value: 'other', label: language === 'ar' ? 'أخرى' : 'Other' },
    ]
  };

  const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16'];

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      const [txRes, casesRes] = await Promise.all([
        transactionsAPI.getAll(),
        casesAPI.getAll()
      ]);
      setTransactions(txRes.data);
      setCases(casesRes.data);

      // Fetch analytics data for admin
      if (isAdmin) {
        const [dashboardRes, analyticsRes, perfRes, expenseRes, alertsRes] = await Promise.all([
          api.get('/finance/dashboard'),
          api.get('/finance/analytics?period=monthly'),
          api.get('/finance/employee-performance'),
          api.get('/finance/expense-breakdown'),
          api.get('/finance/alerts')
        ]);
        setDashboard(dashboardRes.data);
        setAnalytics(analyticsRes.data);
        setEmployeePerformance(perfRes.data);
        setExpenseBreakdown(expenseRes.data);
        setAlerts(alertsRes.data);
      }
    } catch (error) {
      console.error('Fetch error:', error);
      toast.error(t('error'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        amount: parseFloat(formData.amount),
        case_id: formData.case_id || null
      };
      
      if (editingTransaction) {
        await transactionsAPI.update(editingTransaction.id, data);
        toast.success(language === 'ar' ? 'تم تحديث المعاملة' : 'Transaction updated');
      } else {
        await transactionsAPI.create(data);
        toast.success(language === 'ar' ? 'تم إضافة المعاملة' : 'Transaction added');
      }
      setDialogOpen(false);
      resetForm();
      fetchAllData();
    } catch (error) {
      console.error('Submit error:', error);
      toast.error(t('error'));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(language === 'ar' ? 'هل أنت متأكد؟' : 'Are you sure?')) return;
    try {
      await transactionsAPI.delete(id);
      toast.success(language === 'ar' ? 'تم حذف المعاملة' : 'Transaction deleted');
      fetchAllData();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(t('error'));
    }
  };

  const resetForm = () => {
    setFormData({
      transaction_type: 'income',
      amount: '',
      description: '',
      case_id: '',
      category: '',
      date: new Date().toISOString().split('T')[0]
    });
    setEditingTransaction(null);
  };

  const openEditDialog = (tx) => {
    setEditingTransaction(tx);
    setFormData({
      transaction_type: tx.transaction_type,
      amount: tx.amount.toString(),
      description: tx.description,
      case_id: tx.case_id || '',
      category: tx.category || '',
      date: tx.date
    });
    setDialogOpen(true);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat(language === 'ar' ? 'ar-IQ' : 'en-US', {
      style: 'decimal',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount) + ' د.ع.';
  };

  const exportPDF = async (period = 'all') => {
    try {
      const response = await api.get(`/finance/export-pdf?period=${period}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `financial_report_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(language === 'ar' ? 'تم تصدير التقرير' : 'Report exported');
    } catch (error) {
      console.error('Export error:', error);
      toast.error(language === 'ar' ? 'فشل تصدير التقرير' : 'Failed to export report');
    }
  };

  const getCaseName = (caseId) => {
    const caseItem = cases.find(c => c.id === caseId);
    return caseItem ? `${caseItem.case_number} - ${caseItem.title}` : '-';
  };

  const getCategoryLabel = (category, type) => {
    const cats = categories[type] || [];
    const found = cats.find(c => c.value === category);
    return found ? found.label : category || '-';
  };

  const totalIncome = transactions
    .filter(t => t.transaction_type === 'income')
    .reduce((sum, t) => sum + t.amount, 0);
  
  const totalExpenses = transactions
    .filter(t => t.transaction_type === 'expense')
    .reduce((sum, t) => sum + t.amount, 0);

  const filteredTransactions = transactions.filter(t => {
    const matchesSearch = t.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = typeFilter === 'all' || t.transaction_type === typeFilter;
    return matchesSearch && matchesType;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="w-8 h-8 border-4 border-accent border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="finance-page">
      <Header title={t('finance')} />
      
      <div className="p-6 space-y-6">
        {/* Admin Tabs */}
        {isAdmin && (
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <div className="flex items-center justify-between mb-4">
              <TabsList>
                <TabsTrigger value="overview" className="gap-2">
                  <BarChart3 className="w-4 h-4" />
                  {language === 'ar' ? 'نظرة عامة' : 'Overview'}
                </TabsTrigger>
                <TabsTrigger value="analytics" className="gap-2">
                  <TrendingUp className="w-4 h-4" />
                  {language === 'ar' ? 'التحليلات' : 'Analytics'}
                </TabsTrigger>
                <TabsTrigger value="performance" className="gap-2">
                  <Users className="w-4 h-4" />
                  {language === 'ar' ? 'أداء الموظفين' : 'Performance'}
                </TabsTrigger>
                <TabsTrigger value="transactions" className="gap-2">
                  <DollarSign className="w-4 h-4" />
                  {language === 'ar' ? 'المعاملات' : 'Transactions'}
                </TabsTrigger>
              </TabsList>
              
              {/* Export PDF Button */}
              <div className="flex items-center gap-2">
                <Select onValueChange={(period) => exportPDF(period)}>
                  <SelectTrigger className="w-[180px]" data-testid="export-pdf-btn">
                    <FileDown className="w-4 h-4 mr-2" />
                    {language === 'ar' ? 'تصدير PDF' : 'Export PDF'}
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="30">{language === 'ar' ? 'آخر 30 يوم' : 'Last 30 days'}</SelectItem>
                    <SelectItem value="90">{language === 'ar' ? 'آخر 90 يوم' : 'Last 90 days'}</SelectItem>
                    <SelectItem value="365">{language === 'ar' ? 'آخر سنة' : 'Last year'}</SelectItem>
                    <SelectItem value="all">{language === 'ar' ? 'كل الفترات' : 'All time'}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Overview Tab */}
            <TabsContent value="overview" className="space-y-6">
              {/* Alerts Section */}
              {alerts.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {alerts.map((alert, idx) => (
                    <Card key={idx} className={`border-l-4 ${
                      alert.type === 'danger' ? 'border-l-red-500 bg-red-50 dark:bg-red-900/10' :
                      alert.type === 'warning' ? 'border-l-yellow-500 bg-yellow-50 dark:bg-yellow-900/10' :
                      'border-l-green-500 bg-green-50 dark:bg-green-900/10'
                    }`}>
                      <CardContent className="pt-4">
                        <div className="flex items-start gap-3">
                          {alert.type === 'danger' ? (
                            <XCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                          ) : alert.type === 'warning' ? (
                            <AlertTriangle className="w-5 h-5 text-yellow-500 flex-shrink-0" />
                          ) : (
                            <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                          )}
                          <div>
                            <p className="font-medium text-sm">{alert.title}</p>
                            <p className="text-xs text-muted-foreground mt-1">{alert.message}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}

              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card className="stat-card">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">{t('totalIncome')}</p>
                        <h3 className="text-2xl font-bold mt-1 text-emerald-600 dark:text-emerald-400">
                          {formatCurrency(dashboard?.summary?.total_income || totalIncome)}
                        </h3>
                        {dashboard?.growth?.income_growth !== 0 && (
                          <p className={`text-xs mt-1 ${dashboard?.growth?.income_growth > 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                            {dashboard?.growth?.income_growth > 0 ? '+' : ''}{dashboard?.growth?.income_growth}% {language === 'ar' ? 'من الشهر الماضي' : 'from last month'}
                          </p>
                        )}
                      </div>
                      <div className="p-3 rounded-sm bg-emerald-500/10">
                        <ArrowUpCircle className="w-6 h-6 text-emerald-500" strokeWidth={1.5} />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="stat-card">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">{t('totalExpenses')}</p>
                        <h3 className="text-2xl font-bold mt-1 text-red-600 dark:text-red-400">
                          {formatCurrency(dashboard?.summary?.total_expenses || totalExpenses)}
                        </h3>
                        {dashboard?.growth?.expense_growth !== 0 && (
                          <p className={`text-xs mt-1 ${dashboard?.growth?.expense_growth > 0 ? 'text-red-500' : 'text-emerald-500'}`}>
                            {dashboard?.growth?.expense_growth > 0 ? '+' : ''}{dashboard?.growth?.expense_growth}% {language === 'ar' ? 'من الشهر الماضي' : 'from last month'}
                          </p>
                        )}
                      </div>
                      <div className="p-3 rounded-sm bg-red-500/10">
                        <ArrowDownCircle className="w-6 h-6 text-red-500" strokeWidth={1.5} />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="stat-card">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">{t('netProfit')}</p>
                        <h3 className={`text-2xl font-bold mt-1 ${
                          (dashboard?.summary?.net_profit || (totalIncome - totalExpenses)) >= 0 
                            ? 'text-emerald-600 dark:text-emerald-400' 
                            : 'text-red-600 dark:text-red-400'
                        }`}>
                          {formatCurrency(dashboard?.summary?.net_profit || (totalIncome - totalExpenses))}
                        </h3>
                      </div>
                      <div className="p-3 rounded-sm bg-[hsl(var(--accent))]/10">
                        {(dashboard?.summary?.net_profit || (totalIncome - totalExpenses)) >= 0 ? (
                          <TrendingUp className="w-6 h-6 text-[hsl(var(--accent))]" strokeWidth={1.5} />
                        ) : (
                          <TrendingDown className="w-6 h-6 text-red-500" strokeWidth={1.5} />
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="stat-card">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">{language === 'ar' ? 'هامش الربح' : 'Profit Margin'}</p>
                        <h3 className="text-2xl font-bold mt-1 text-[hsl(var(--accent))]">
                          {dashboard?.summary?.profit_margin || 0}%
                        </h3>
                      </div>
                      <div className="p-3 rounded-sm bg-[hsl(var(--accent))]/10">
                        <PieChart className="w-6 h-6 text-[hsl(var(--accent))]" strokeWidth={1.5} />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Charts Row */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Revenue vs Expenses Chart */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <BarChart3 className="w-5 h-5 text-[hsl(var(--accent))]" />
                      {language === 'ar' ? 'الإيرادات vs المصروفات' : 'Revenue vs Expenses'}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {analytics.length > 0 ? (
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={analytics.slice(-6)}>
                          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                          <XAxis dataKey="period" tick={{ fill: 'hsl(var(--muted-foreground))' }} />
                          <YAxis tick={{ fill: 'hsl(var(--muted-foreground))' }} />
                          <Tooltip 
                            contentStyle={{ 
                              backgroundColor: 'hsl(var(--card))', 
                              border: '1px solid hsl(var(--border))',
                              borderRadius: '8px'
                            }}
                            formatter={(value) => formatCurrency(value)}
                          />
                          <Legend />
                          <Bar dataKey="income" name={language === 'ar' ? 'إيرادات' : 'Income'} fill="#10b981" radius={[4, 4, 0, 0]} />
                          <Bar dataKey="expense" name={language === 'ar' ? 'مصروفات' : 'Expenses'} fill="#ef4444" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                        {language === 'ar' ? 'لا توجد بيانات' : 'No data available'}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Expense Breakdown Pie Chart */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <PieChart className="w-5 h-5 text-[hsl(var(--accent))]" />
                      {language === 'ar' ? 'توزيع المصروفات' : 'Expense Breakdown'}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {expenseBreakdown?.categories?.length > 0 ? (
                      <ResponsiveContainer width="100%" height={300}>
                        <RePieChart>
                          <Pie
                            data={expenseBreakdown.categories}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={({ category, percentage }) => `${getCategoryLabel(category, 'expense')} (${percentage}%)`}
                            outerRadius={100}
                            fill="#8884d8"
                            dataKey="total"
                          >
                            {expenseBreakdown.categories.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip formatter={(value) => formatCurrency(value)} />
                        </RePieChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                        {language === 'ar' ? 'لا توجد مصروفات' : 'No expenses'}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Profit Trend */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-[hsl(var(--accent))]" />
                    {language === 'ar' ? 'اتجاه الأرباح' : 'Profit Trend'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {analytics.length > 0 ? (
                    <ResponsiveContainer width="100%" height={250}>
                      <AreaChart data={analytics}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                        <XAxis dataKey="period" tick={{ fill: 'hsl(var(--muted-foreground))' }} />
                        <YAxis tick={{ fill: 'hsl(var(--muted-foreground))' }} />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: 'hsl(var(--card))', 
                            border: '1px solid hsl(var(--border))',
                            borderRadius: '8px'
                          }}
                          formatter={(value) => formatCurrency(value)}
                        />
                        <Area 
                          type="monotone" 
                          dataKey="profit" 
                          name={language === 'ar' ? 'الربح' : 'Profit'}
                          stroke="hsl(var(--accent))" 
                          fill="hsl(var(--accent))" 
                          fillOpacity={0.2}
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[250px] flex items-center justify-center text-muted-foreground">
                      {language === 'ar' ? 'لا توجد بيانات' : 'No data available'}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Analytics Tab */}
            <TabsContent value="analytics" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>{language === 'ar' ? 'التحليلات الشهرية التفصيلية' : 'Detailed Monthly Analytics'}</CardTitle>
                </CardHeader>
                <CardContent>
                  {analytics.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th>{language === 'ar' ? 'الفترة' : 'Period'}</th>
                            <th>{language === 'ar' ? 'الإيرادات' : 'Income'}</th>
                            <th>{language === 'ar' ? 'المصروفات' : 'Expenses'}</th>
                            <th>{language === 'ar' ? 'الربح' : 'Profit'}</th>
                            <th>{language === 'ar' ? 'عدد المعاملات' : 'Transactions'}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {analytics.map((item, idx) => (
                            <tr key={idx}>
                              <td className="font-medium">{item.period}</td>
                              <td className="text-emerald-600">{formatCurrency(item.income)}</td>
                              <td className="text-red-600">{formatCurrency(item.expense)}</td>
                              <td className={item.profit >= 0 ? 'text-emerald-600 font-semibold' : 'text-red-600 font-semibold'}>
                                {formatCurrency(item.profit)}
                              </td>
                              <td>{(item.income_count || 0) + (item.expense_count || 0)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="py-12 text-center text-muted-foreground">
                      {language === 'ar' ? 'لا توجد بيانات' : 'No data available'}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Performance Tab */}
            <TabsContent value="performance" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="w-5 h-5 text-[hsl(var(--accent))]" />
                    {language === 'ar' ? 'أداء الموظفين حسب الإيرادات' : 'Employee Performance by Revenue'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {employeePerformance.length > 0 ? (
                    <>
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={employeePerformance} layout="vertical">
                          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                          <XAxis type="number" tick={{ fill: 'hsl(var(--muted-foreground))' }} />
                          <YAxis type="category" dataKey="lawyer_name" tick={{ fill: 'hsl(var(--muted-foreground))' }} width={120} />
                          <Tooltip formatter={(value) => formatCurrency(value)} />
                          <Bar dataKey="total_revenue" name={language === 'ar' ? 'الإيرادات' : 'Revenue'} fill="hsl(var(--accent))" radius={[0, 4, 4, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                      
                      <div className="mt-6 overflow-x-auto">
                        <table className="data-table">
                          <thead>
                            <tr>
                              <th>#</th>
                              <th>{language === 'ar' ? 'الموظف' : 'Employee'}</th>
                              <th>{language === 'ar' ? 'الدور' : 'Role'}</th>
                              <th>{language === 'ar' ? 'عدد القضايا' : 'Cases'}</th>
                              <th>{language === 'ar' ? 'الإيرادات' : 'Revenue'}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {employeePerformance.map((emp, idx) => (
                              <tr key={emp.lawyer_id}>
                                <td>
                                  {idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : idx + 1}
                                </td>
                                <td className="font-medium">{emp.lawyer_name}</td>
                                <td>
                                  <Badge variant="outline">{emp.role}</Badge>
                                </td>
                                <td>{emp.case_count}</td>
                                <td className="text-emerald-600 font-semibold">{formatCurrency(emp.total_revenue)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  ) : (
                    <div className="py-12 text-center text-muted-foreground">
                      {language === 'ar' ? 'لا توجد بيانات أداء' : 'No performance data'}
                      <p className="text-sm mt-2">{language === 'ar' ? 'يتم حساب الأداء بناءً على القضايا المسندة والإيرادات المرتبطة بها' : 'Performance is calculated based on assigned cases and related revenue'}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Transactions Tab */}
            <TabsContent value="transactions">
              {renderTransactionsSection()}
            </TabsContent>
          </Tabs>
        )}

        {/* Non-admin view - just transactions */}
        {!isAdmin && renderTransactionsSection()}
      </div>
    </div>
  );

  function renderTransactionsSection() {
    return (
      <div className="space-y-6">
        {/* Summary Cards for non-admin or in transactions tab */}
        {!isAdmin && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="stat-card">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">{t('totalIncome')}</p>
                    <h3 className="text-2xl font-bold mt-1 text-emerald-600 dark:text-emerald-400">
                      {formatCurrency(totalIncome)}
                    </h3>
                  </div>
                  <div className="p-3 rounded-sm bg-emerald-500/10">
                    <ArrowUpCircle className="w-6 h-6 text-emerald-500" strokeWidth={1.5} />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="stat-card">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">{t('totalExpenses')}</p>
                    <h3 className="text-2xl font-bold mt-1 text-red-600 dark:text-red-400">
                      {formatCurrency(totalExpenses)}
                    </h3>
                  </div>
                  <div className="p-3 rounded-sm bg-red-500/10">
                    <ArrowDownCircle className="w-6 h-6 text-red-500" strokeWidth={1.5} />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="stat-card">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">{t('netProfit')}</p>
                    <h3 className={`text-2xl font-bold mt-1 ${
                      totalIncome - totalExpenses >= 0 
                        ? 'text-emerald-600 dark:text-emerald-400' 
                        : 'text-red-600 dark:text-red-400'
                    }`}>
                      {formatCurrency(totalIncome - totalExpenses)}
                    </h3>
                  </div>
                  <div className="p-3 rounded-sm bg-[hsl(var(--accent))]/10">
                    {totalIncome - totalExpenses >= 0 ? (
                      <TrendingUp className="w-6 h-6 text-[hsl(var(--accent))]" strokeWidth={1.5} />
                    ) : (
                      <TrendingDown className="w-6 h-6 text-red-500" strokeWidth={1.5} />
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Actions Bar */}
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          <div className="flex flex-1 gap-3 w-full sm:w-auto">
            <div className="relative flex-1 sm:max-w-xs">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder={t('search')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
                data-testid="search-transactions"
              />
            </div>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-[140px]" data-testid="filter-type">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('all')}</SelectItem>
                <SelectItem value="income">{t('income')}</SelectItem>
                <SelectItem value="expense">{t('expense')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {canManageFinance && (
            <Dialog open={dialogOpen} onOpenChange={(open) => {
              setDialogOpen(open);
              if (!open) resetForm();
            }}>
              <DialogTrigger asChild>
                <Button className="btn-accent gap-2" data-testid="add-transaction-btn">
                  <Plus className="w-4 h-4" />
                  {t('addTransaction')}
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>
                    {editingTransaction ? t('edit') : t('addTransaction')}
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label>{t('transactionType')} *</Label>
                    <Tabs 
                      value={formData.transaction_type} 
                      onValueChange={(v) => setFormData({...formData, transaction_type: v, category: ''})}
                    >
                      <TabsList className="grid w-full grid-cols-2">
                        <TabsTrigger value="income" className="gap-2" data-testid="type-income">
                          <ArrowUpCircle className="w-4 h-4" />
                          {t('income')}
                        </TabsTrigger>
                        <TabsTrigger value="expense" className="gap-2" data-testid="type-expense">
                          <ArrowDownCircle className="w-4 h-4" />
                          {t('expense')}
                        </TabsTrigger>
                      </TabsList>
                    </Tabs>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>{t('amount')} *</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={formData.amount}
                        onChange={(e) => setFormData({...formData, amount: e.target.value})}
                        required
                        data-testid="transaction-amount-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>{t('date')} *</Label>
                      <Input
                        type="date"
                        value={formData.date}
                        onChange={(e) => setFormData({...formData, date: e.target.value})}
                        required
                        data-testid="transaction-date-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>{t('category')}</Label>
                    <Select 
                      value={formData.category || "none"} 
                      onValueChange={(v) => setFormData({...formData, category: v === "none" ? "" : v})}
                    >
                      <SelectTrigger data-testid="transaction-category-select">
                        <SelectValue placeholder={t('category')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">{language === 'ar' ? 'بدون فئة' : 'No category'}</SelectItem>
                        {categories[formData.transaction_type].map(cat => (
                          <SelectItem key={cat.value} value={cat.value}>
                            {cat.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>{language === 'ar' ? 'القضية المرتبطة' : 'Related Case'}</Label>
                    <Select 
                      value={formData.case_id || "none"} 
                      onValueChange={(v) => setFormData({...formData, case_id: v === "none" ? "" : v})}
                    >
                      <SelectTrigger data-testid="transaction-case-select">
                        <SelectValue placeholder={language === 'ar' ? 'اختر قضية' : 'Select case'} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">{language === 'ar' ? 'بدون قضية' : 'No case'}</SelectItem>
                        {cases.map(c => (
                          <SelectItem key={c.id} value={c.id}>
                            {c.case_number} - {c.title}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>{t('description')} *</Label>
                    <Textarea
                      value={formData.description}
                      onChange={(e) => setFormData({...formData, description: e.target.value})}
                      required
                      rows={2}
                      data-testid="transaction-description-input"
                    />
                  </div>

                  <div className="flex justify-end gap-3 pt-4">
                    <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                      {t('cancel')}
                    </Button>
                    <Button type="submit" className="btn-accent" data-testid="transaction-submit-btn">
                      {t('save')}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {/* Transactions Table */}
        <Card data-testid="transactions-table">
          <CardContent className="p-0">
            {filteredTransactions.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>{t('date')}</th>
                      <th>{t('transactionType')}</th>
                      <th>{t('description')}</th>
                      <th>{t('category')}</th>
                      <th>{language === 'ar' ? 'القضية' : 'Case'}</th>
                      <th>{t('amount')}</th>
                      {isAdmin && <th>{t('actions')}</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredTransactions.map((tx) => (
                      <tr key={tx.id}>
                        <td className="text-sm">{tx.date}</td>
                        <td>
                          {tx.transaction_type === 'income' ? (
                            <Badge className="bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400">
                              <ArrowUpCircle className="w-3 h-3 mr-1" />
                              {t('income')}
                            </Badge>
                          ) : (
                            <Badge className="bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400">
                              <ArrowDownCircle className="w-3 h-3 mr-1" />
                              {t('expense')}
                            </Badge>
                          )}
                        </td>
                        <td className="max-w-[200px] truncate">{tx.description}</td>
                        <td>{getCategoryLabel(tx.category, tx.transaction_type)}</td>
                        <td className="text-sm text-muted-foreground max-w-[150px] truncate">
                          {tx.case_id ? getCaseName(tx.case_id) : '-'}
                        </td>
                        <td className={`font-semibold ${
                          tx.transaction_type === 'income' 
                            ? 'text-emerald-600 dark:text-emerald-400' 
                            : 'text-red-600 dark:text-red-400'
                        }`}>
                          {tx.transaction_type === 'income' ? '+' : '-'}{formatCurrency(tx.amount)}
                        </td>
                        {isAdmin && (
                          <td>
                            <div className="flex items-center gap-1">
                              <Button 
                                variant="ghost" 
                                size="icon"
                                onClick={() => openEditDialog(tx)}
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="icon"
                                className="text-destructive"
                                onClick={() => handleDelete(tx.id)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="p-12 text-center">
                <DollarSign className="w-12 h-12 mx-auto text-muted-foreground mb-4" strokeWidth={1} />
                <p className="text-muted-foreground">{t('noData')}</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }
};

export default FinancePage;
